/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import gridBackground from '../../../../../assets/grid-background.svg';
import { useAnalyticsEvents } from '../../../../../common/analytics/generated/use-analytics-events';
import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { GraphExplorer } from '../../../../../graph-explorer';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../../SpacingWrapper';

import { messages } from './messages';

const styles = cssMap({
	section: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
	},
	contentWrapper: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		paddingInline: token('space.400'),
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		gap: '3.75rem',
	},
	statsRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		paddingInline: token('space.400'),
		paddingBlock: token('space.600'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	bigNumber: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '160px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1,
		margin: 0,
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	bigNumberMobile: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '80px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 400,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1,
		margin: 0,
		display: 'none',
		'@media (max-width: 30rem)': {
			display: 'unset',
		},
	},
	bigNumberLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
		font: token('font.body.large'),
		fontFamily: token('font.family.brand.body'),
		margin: 0,
	},
	description: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#6B6E76',
		font: token('font.body.large'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		margin: 0,
		width: '100%',
		maxWidth: '800px',
	},
	graphExplorer: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		borderRadius: token('radius.xlarge'),
		height: '780px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		position: 'relative',
		'@media (max-width: 30rem)': {
			height: '540px',
		},
	},
	graphInner: {
		width: '100%',
		height: '100%',
	},
	expandCollapseButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// Sit above the graph canvas so it's always clickable.
		zIndex: 1,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#96999E',
	},
	// ── Near-fullscreen expanded overlay ────────────────────────────────
	overlayBackdrop: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// Small breathing room so the overlay isn't flush with the viewport edge.
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '24px',
		// Sit above all in-page content (modals, popovers, etc.).
		zIndex: 1000,
	},
	overlayPanel: {
		width: '100%',
		height: '100%',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#101214',
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#303134',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
	},
	overlayCloseButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 2,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#96999E',
	},
	overlayContent: {
		flex: 1,
		minHeight: '0px',
		width: '100%',
	},
});

export function GraphPreviewSection({ metrics }: { metrics: OrgMetricsResponse }): JSX.Element {
	const { formatMessage, formatNumber } = useIntl();
	const titleId = useId();
	const descriptionId = useId();
	const [graphIsExpanded, setGraphIsExpanded] = useState<boolean>(false);
	// React refs for the two possible host containers for the persistent
	// portal target node. Whichever one is mounted "owns" the host for that
	// render: collapsed → inline host; expanded → fullscreen overlay host.
	const inlineHostRef = useRef<HTMLDivElement | null>(null);
	const overlayHostRef = useRef<HTMLDivElement | null>(null);
	// Stores the element that had focus before the modal opened so we can
	// restore focus to it when the modal closes. FocusLock also handles this,
	// but we keep an explicit reference as a defensive fallback (the trigger
	// `IconButton` is briefly disabled while expanded, which can confuse
	// FocusLock's restoration in some browsers).
	const lastFocusedRef = useRef<HTMLElement | null>(null);

	const { fireEvent } = useAnalyticsEvents();
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.GraphPreview);

	const openExpanded = useCallback(() => {
		fireEvent('track.graphPreview.openedExplorer.twgMicrositeGraphPreview', {});
		const doc = getDocument();
		const active = doc?.activeElement;
		lastFocusedRef.current = active instanceof HTMLElement ? active : null;
		setGraphIsExpanded(true);
	}, [fireEvent]);
	const closeExpanded = useCallback(() => {
		fireEvent('track.graphPreview.closedExplorer.twgMicrositeGraphPreview', {});
		setGraphIsExpanded(false);
	}, [fireEvent]);

	// Persistent portal target. We create this DOM node exactly once and
	// physically re-parent it between the inline host and the overlay host
	// when the user expands/collapses. Because React renders the
	// `<GraphExplorer />` *into* this node via `createPortal`, the explorer's
	// React subtree, Jotai store, in-flight queries, and graph state are all
	// preserved across the move — no unmount, no reload.
	const portalNodeRef = useRef<HTMLDivElement | null>(null);
	if (portalNodeRef.current === null) {
		const doc = getDocument();
		if (doc) {
			const node = doc.createElement('div');
			node.setAttribute('data-twg-graph-explorer-portal', '');
			node.style.width = '100%';
			node.style.height = '100%';
			portalNodeRef.current = node;
		}
	}

	// Re-parent the persistent portal target to the active host whenever the
	// expand state changes (or the host refs become available on first mount).
	// We do this in a layout effect so the move happens before paint, avoiding
	// a single-frame flash of an empty container.
	useLayoutEffect(() => {
		const node = portalNodeRef.current;
		const target = graphIsExpanded ? overlayHostRef.current : inlineHostRef.current;
		if (!node || !target) {
			return;
		}
		if (node.parentNode !== target) {
			target.appendChild(node);
		}
	}, [graphIsExpanded]);

	// Detach the persistent portal node from the DOM when the section
	// unmounts so we don't leak it.
	useEffect(() => {
		return () => {
			const node = portalNodeRef.current;
			if (node && node.parentNode) {
				node.parentNode.removeChild(node);
			}
		};
	}, []);

	// Close on Escape and lock body scroll while the overlay is open.
	useEffect(() => {
		if (!graphIsExpanded) {
			return;
		}
		const doc = getDocument();
		const body = doc?.body;
		const previousOverflow = body?.style.overflow ?? '';
		if (body) {
			body.style.overflow = 'hidden';
		}
		const unbind = bind(window, {
			type: 'keydown',
			listener: (event: KeyboardEvent) => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					closeExpanded();
				}
			},
			// Capture so we win against any descendant key handler inside the
			// graph explorer canvas (e.g. xyflow's built-in shortcuts).
			options: { capture: true },
		});
		return () => {
			unbind();
			if (body) {
				body.style.overflow = previousOverflow;
			}
		};
	}, [graphIsExpanded, closeExpanded]);

	// Mark sibling page content as inert + aria-hidden while the modal is
	// open so assistive tech and pointer/keyboard input don't reach the
	// background. We mark every direct child of <body> except the one that
	// contains our overlay (which is the SpacingWrapper at this section's
	// position in the DOM). FocusLock additionally enforces tab-trapping.
	useEffect(() => {
		if (!graphIsExpanded) {
			return;
		}
		const doc = getDocument();
		const body = doc?.body;
		const overlayHost = overlayHostRef.current;
		if (!body || !overlayHost) {
			return;
		}
		const previous: Array<{
			node: HTMLElement;
			ariaHidden: string | null;
			inert: string | null;
		}> = [];
		Array.from(body.children).forEach((child) => {
			if (!(child instanceof HTMLElement)) {
				return;
			}
			// Don't hide the subtree that actually contains the open overlay.
			if (child.contains(overlayHost)) {
				return;
			}
			previous.push({
				node: child,
				ariaHidden: child.getAttribute('aria-hidden'),
				inert: child.getAttribute('inert'),
			});
			child.setAttribute('aria-hidden', 'true');
			child.setAttribute('inert', '');
		});
		return () => {
			previous.forEach(({ node, ariaHidden, inert }) => {
				if (ariaHidden === null) {
					node.removeAttribute('aria-hidden');
				} else {
					node.setAttribute('aria-hidden', ariaHidden);
				}
				if (inert === null) {
					node.removeAttribute('inert');
				} else {
					node.setAttribute('inert', inert);
				}
			});
		};
	}, [graphIsExpanded]);

	// Defensive focus-restore fallback (see lastFocusedRef comment above).
	useEffect(() => {
		if (graphIsExpanded) {
			return;
		}
		const target = lastFocusedRef.current;
		if (target && typeof target.focus === 'function') {
			const id = window.setTimeout(() => target.focus(), 0);
			return () => window.clearTimeout(id);
		}
	}, [graphIsExpanded]);

	const portalNode = portalNodeRef.current;

	return (
		<section
			ref={sectionRef}
			css={styles.section}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ backgroundImage: `url(${gridBackground})` }}
		>
			<SpacingWrapper>
				<div css={styles.contentWrapper}>
					<div css={styles.content}>
						<div css={styles.statsRow}>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumberLabel}>{formatMessage(messages.relationshipsCreatedLabel)}</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumber}>{formatNumber(metrics.totalRelationships ?? 0)}</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.bigNumberMobile}>
								{formatNumber(metrics.totalRelationships ?? 0, {
									notation: 'compact',
									maximumFractionDigits: 2,
								})}
							</p>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.description}>{formatMessage(messages.description)}</p>
						</div>

						{/*
						 * Explorer Light placeholder — replaced with live component in data
						 * phase. The wrapper does NOT take role="img" because it contains a
						 * focusable IconButton (axe rule: nested-interactive). The visual
						 * placeholder is described to AT via VisuallyHidden text below
						 * instead, when the explorer hasn't loaded yet.
						 */}
						<div css={styles.graphExplorer}>
							<VisuallyHidden>{formatMessage(messages.explorerPlaceholderAlt)}</VisuallyHidden>
							{/*
							 * Inline host for the persistent portal target. When collapsed,
							 * the portal target lives here; when expanded it is moved to
							 * the overlay host below (without unmounting React's portal
							 * children).
							 */}
							<div css={styles.graphInner} ref={inlineHostRef} />
							<div css={styles.expandCollapseButton}>
								<IconButton
									icon={GrowDiagonalIcon}
									label={formatMessage(messages.expandLabel)}
									onClick={openExpanded}
									isDisabled={graphIsExpanded}
								/>
							</div>
						</div>

						{/*
						 * Render the persistent GraphExplorer into the persistent portal
						 * target. This portal element exists for the entire section
						 * lifetime regardless of expand state, so the explorer never
						 * remounts on expand/collapse — the *target node* is what moves
						 * in the DOM, not the React subtree.
						 */}
						{portalNode && createPortal(<GraphExplorer />, portalNode)}

						{graphIsExpanded && (
							<FocusLock returnFocus autoFocus>
								<div css={styles.overlayBackdrop}>
									<div
										css={styles.overlayPanel}
										role="dialog"
										aria-modal="true"
										aria-labelledby={titleId}
										aria-describedby={descriptionId}
										tabIndex={-1}
									>
										{/*
										 * Visually hidden but exposed to assistive tech to label
										 * and describe the dialog. Screen readers announce the
										 * heading and description when focus enters the dialog.
										 */}
										<VisuallyHidden>
											<Heading id={titleId} size="medium" as="h2">
												{formatMessage(messages.modalTitle)}
											</Heading>
											<Text id={descriptionId} as="p">
												{formatMessage(messages.modalDescription)}
											</Text>
										</VisuallyHidden>
										<div css={styles.overlayCloseButton}>
											<IconButton
												icon={ShrinkDiagonalIcon}
												label={formatMessage(messages.closeLabel)}
												onClick={closeExpanded}
												appearance="subtle"
											/>
										</div>
										{/* Overlay host for the persistent portal target while expanded. */}
										<div css={styles.overlayContent} ref={overlayHostRef} />
									</div>
								</div>
							</FocusLock>
						)}
					</div>
				</div>
			</SpacingWrapper>
		</section>
	);
}
